/* Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useState } from "react";
import { Else, If, Then } from "react-if";
import styled from "styled-components";
import { Button, ExtendedPopover, Sidebar, theme } from "@nordcloud/gnui";
import { ButtonGroup } from "~/components";
import { useQueryState } from "~/hooks";
import { FiltersProp } from "~/models/estateRecordRow";
import { useDatadogRUM } from "~/services/datadog";
import { isNotEmpty, noop } from "~/tools";
import { DisabledFilters, EstateFilters } from "./EstateFilters";
import { EstateFiltersToggleSidebar, PredefinedFilters } from "./SearchPresets";

type QueryState = FiltersProp & {
  page?: number;
  limit?: number;
  query?: string;
};

type SearchPresetV2 = React.ComponentProps<
  typeof PredefinedFilters
>["items"][number];

type Props = {
  isAccount?: boolean;
  disabledFilters?: DisabledFilters[];
  searchPresets?: SearchPresetV2[];
  onApply: () => void;
  onSearchPresetApply?: (searchPreset: SearchPresetV2) => void;
  allowSavingFilters: boolean;
  isDisabled?: boolean;
};

export function EstateFiltersToggle({
  isAccount = false,
  onApply,
  allowSavingFilters,
  onSearchPresetApply = noop,
  disabledFilters = [],
  searchPresets = [],
  isDisabled = false,
}: Props) {
  const [isFiltersSidebarOpen, setIsFiltersSidebarOpen] = useState(false);
  const [isPresetsSidebarOpen, setIsPresetsSidebarOpen] = useState(false);

  const ddRum = useDatadogRUM();

  const {
    state: {
      limit: ignoredLimit,
      page: ignoredPage,
      query: ignoredQuery,
      ...restFilters
    },
  } = useQueryState<QueryState>();

  const [filters, setFilters] = useState<FiltersProp>(restFilters);

  const toggleFilters = (openFilters: boolean) => {
    setFilters(restFilters);
    setIsFiltersSidebarOpen(openFilters);
  };

  const showPredefinedFilters = isNotEmpty(searchPresets) && allowSavingFilters;

  return (
    <>
      <If condition={showPredefinedFilters}>
        <Then>
          <ButtonGroup>
            <Button
              disabled={isDisabled}
              severity="low"
              icon="filter"
              onClick={() => {
                ddRum?.addAction("Test: opened estate filters");
                toggleFilters(true);
              }}
            >
              Filters
            </Button>
            <ExtendedPopover
              position="end"
              placement="bottom"
              triggerOn={isDisabled ? undefined : "hover"}
              closeOn="hover"
              trigger={
                <ExpandButton
                  severity="low"
                  icon="caretDown"
                  disabled={isDisabled}
                />
              }
              content={
                <PredefinedFilters
                  items={searchPresets}
                  onShowAll={() => setIsPresetsSidebarOpen(true)}
                  onClick={(searchPreset) => onSearchPresetApply(searchPreset)}
                />
              }
            />
          </ButtonGroup>
        </Then>

        <Else>
          <Button
            disabled={isDisabled}
            severity="low"
            icon="filter"
            onClick={() => toggleFilters(true)}
          >
            Filters
          </Button>
        </Else>
      </If>

      <Sidebar
        title="Filter Results"
        isOpen={isFiltersSidebarOpen}
        onClick={() => toggleFilters(false)}
      >
        <EstateFilters
          filters={filters}
          setFilters={setFilters}
          disabledFilters={disabledFilters}
          isAccount={isAccount}
          onApply={() => {
            toggleFilters(false);
            onApply();
          }}
        />
      </Sidebar>
      <EstateFiltersToggleSidebar
        isOpen={isPresetsSidebarOpen}
        onSearchPresetApply={onSearchPresetApply}
        onClose={() => setIsPresetsSidebarOpen(false)}
      />
    </>
  );
}

const ExpandButton = styled(Button)`
  border-top-right-radius: ${theme.radiusDefault} !important;
  border-bottom-right-radius: ${theme.radiusDefault} !important;
`;
